import React, { useEffect, useState } from "react";
import Layout from "../../components/layout";
import { FaArrowLeft, FaSearch, FaCalendarAlt } from "react-icons/fa";

import styles from "./ActivityLogsPage.module.scss";
import { useNavigate } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchActivityLogsData,
  getActivityLogs,
  getTotalActivityLogs,
  getActivityLogsUsers,
} from "../../features/activityLogs/activityLogsSlice";
import Pagination from "../../components/pagination";
import { selectStyles, toastStyles } from "../../static/styles";
import { toast } from "react-toastify";
import { POST } from "../../services/axios.service";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import ActivityLogList from "../../components/activityLogs/ActivityLogList";
import useConfirmPopup from "../../context/useConfirmPopup";

type OptionsProps = {
  value: string | number;
  label: string;
};

const modelOptions: OptionsProps[] = [
  { value: "Account", label: "એકાઉન્ટ" },
  { value: "AccountType", label: "એકાઉન્ટ ટાઈપ" },
  { value: "Attendance", label: "હાજરી" },
  { value: "Employees", label: "કામદાર" },
  { value: "Items", label: "આઈટમ" },
  { value: "Orders", label: "ઓર્ડર" },
  { value: "Parties", label: "પાર્ટી" },
  { value: "PartiesItems", label: "પાર્ટી આઈટમ" },
  { value: "Payments", label: "પેમેન્ટ" },
  { value: "Processes", label: "પ્રોસેસ" },
  { value: "Purchase", label: "ખરીદી" },
  { value: "PurchaseDetail", label: "ખરીદી ડિટેઈલ" },
  { value: "PurchaseItems", label: "ખરીદી આઈટમ" },
  { value: "PurchaseParties", label: "ખરીદી પાર્ટી" },
  { value: "PurchasePayments", label: "ખરીદી પેમેન્ટ" },
  { value: "Salaries", label: "પગાર" },
  { value: "User", label: "યુઝર" },
];

const actionOptions: OptionsProps[] = [
  { value: "update", label: "અપડેટ" },
  { value: "delete", label: "ડિલીટ" },
];

const ActivityLogsPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const confirmAction = useConfirmPopup();
  const logs = useAppSelector(getActivityLogs);
  const totalLogs = useAppSelector(getTotalActivityLogs);
  const users = useAppSelector(getActivityLogsUsers);

  const [userOptions, setUserOptions] = useState<OptionsProps[]>([]);

  interface FilterFormInput {
    fromDate: string;
    toDate: string;
    action: string;
    model_type: string;
    user_id: number;
  }

  const { handleSubmit, register, reset, getValues, control, setValue } =
    useForm<FilterFormInput>();

  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);

  const hanldeGoToHome = () => {
    navigate(route.dashboard);
  };

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const verifyAccess = async () => {
    const verified = sessionStorage.getItem("activityLogVerified");
    if (verified === "true") {
      setIsVerified(true);
      return;
    }

    const response = await confirmAction(
      "એક્ટિવિટી લોગ",
      "એડમીન નો પાસવર્ડ દાખલ કરો",
      "ચકાસો",
      true
    );

    if (response.confirmed) {
      try {
        const res: any = await POST("/v1/admin/activity-logs/verify", {
          password: response.password,
        });

        if (res.data.status === "success") {
          sessionStorage.setItem("activityLogVerified", "true");
          setIsVerified(true);
        } else {
          toast.error(res.data.message, { ...(toastStyles as any) });
          navigate(route.dashboard);
        }
      } catch (error: any) {
        toast.error(
          error?.data?.message ||
            "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
        navigate(route.dashboard);
      }
    } else {
      navigate(route.dashboard);
    }
  };

  const handleOnFilterSubmit = async () => {
    if (page === 1) {
      fetchLogsList(1, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleClearFilter = () => {
    reset();
    if (page === 1) {
      fetchLogsList(1, pageSize);
    } else {
      setPage(1);
    }
  };

  const fetchLogsList = async (page: number, size: number) => {
    const data = getValues();

    setIsLoading(true);

    dispatch(
      fetchActivityLogsData({
        page: page,
        size: size,
        fromDate: data?.fromDate || null,
        toDate: data?.toDate || null,
        action: data?.action || null,
        model_type: data?.model_type || null,
        user_id: data?.user_id || null,
      })
    ).then(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    const options =
      users?.map((user) => ({ value: user.id, label: user.name })) ?? [];
    setUserOptions(options);
  }, [users]);

  useEffect(() => {
    if (isVerified) {
      fetchLogsList(page, pageSize);
    }
  }, [page, isVerified]);

  useEffect(() => {
    verifyAccess();
  }, []);

  if (!isVerified) {
    return (
      <Layout>
        <div className={styles.ActivityLogsPage}>
          <div className="flex items-center justify-center h-screen">
            <p className="text-primary-2 font-bold text-xl">રાહ જુઓ...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.ActivityLogsPage}>
        <div className="logoSection">
          <h1
            onClick={hanldeGoToHome}
            className="text-5xl font-bold text-white lg:text-3xl cursor-pointer lg:w-fit"
          >
            કનૈયા પ્લેટિંગ
          </h1>

          <hr className="w-[180px] border-white border-[4px] rounded-[4px] mx-auto mt-4 lg:mx-0 lg:w-24 lg:border-[2px] lg:mt-2" />
        </div>

        <div className="pageTitleSection">
          <FaArrowLeft
            onClick={hanldeGoToHome}
            className="lg:hidden absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
          />

          <h1 className="text-5xl mt-6 lg:mt-0 font-bold text-white lg:text-2xl lg:text-primary-1">
            એક્ટિવિટી લોગ ની વિગતો
          </h1>
        </div>

        <div className="mt-[60px] px-10 py-5">
          <div className="lg:flex justify-between gap-4 items-center mb-4">
            <div className="w-full lg:w-fit lg:mt-4">
              <p className="text-primary-1 font-bold">
                ટોટલ રેકોર્ડ : {totalLogs}
              </p>
            </div>

            <div className="mt-4 lg:mt-0 w-full lg:w-fit flex justify-between lg:justify-end gap-4">
              <button
                className="p-2 rounded-md flex items-center bg-gradient-to-r from-primary-1 to-primary-2 text-white transition-colors duration-300"
                onClick={() => setShowFilter(!showFilter)}
              >
                <FaSearch className="mr-2" />{" "}
                {showFilter ? "ફિલ્ટર છુપાવો" : "ફિલ્ટર બતાવો"}
              </button>
            </div>
          </div>

          {showFilter && (
            <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
              <div className="mb-4 bg-white shadow rounded p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded pr-[2px] h-[44px]">
                    <FaCalendarAlt className="text-white mx-2" />
                    <input
                      className="bg-white text-primary-1 placeholder:text-primary-2 font-semibold text-xl focus:outline-none p-2 rounded flex-grow h-[40px]"
                      type="date"
                      {...register("fromDate")}
                      placeholder="From Date"
                    />
                  </div>

                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded pr-[2px] h-[44px]">
                    <FaCalendarAlt className="text-white mx-2" />
                    <input
                      className="bg-white text-primary-1 placeholder:text-primary-2 font-semibold text-xl focus:outline-none p-2 rounded flex-grow h-[40px]"
                      type="date"
                      {...register("toDate")}
                      placeholder="To Date"
                    />
                  </div>

                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                    <Controller
                      {...register("action")}
                      control={control}
                      render={({ field }) => (
                        <Select
                          isClearable
                          {...field}
                          value={
                            actionOptions.find(
                              (val) => val.value === field.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"એકશન સિલેક્ટ કરો"}
                          options={actionOptions}
                          onChange={(val: any) =>
                            setValue("action", val?.value || "")
                          }
                        />
                      )}
                    />
                  </div>

                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                    <Controller
                      {...register("model_type")}
                      control={control}
                      render={({ field }) => (
                        <Select
                          isClearable
                          {...field}
                          value={
                            modelOptions.find(
                              (val) => val.value === field.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"મોડ્યુલ સિલેક્ટ કરો"}
                          options={modelOptions}
                          onChange={(val: any) =>
                            setValue("model_type", val?.value || "")
                          }
                        />
                      )}
                    />
                  </div>

                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                    <Controller
                      {...register("user_id")}
                      control={control}
                      render={({ field }) => (
                        <Select
                          isClearable
                          {...field}
                          value={
                            userOptions.find(
                              (val) => val.value === field.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"યુઝર સિલેક્ટ કરો"}
                          options={userOptions}
                          onChange={(val: any) =>
                            setValue("user_id", val?.value)
                          }
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="flex gap-4 justify-end mt-4">
                  <button
                    onClick={handleClearFilter}
                    type="button"
                    className="bg-primary-1 hover:bg-primary-2 text-white px-4 py-2 rounded-md transition duration-300 ease-in-out"
                  >
                    હટાઓ
                  </button>

                  <button
                    type="submit"
                    className="bg-primary-1 hover:bg-primary-2 text-white px-4 py-2 rounded-md transition duration-300 ease-in-out"
                  >
                    ફિલ્ટર લાગુ કરો
                  </button>
                </div>
              </div>
            </form>
          )}

          <ActivityLogList
            logs={logs}
            pageNumber={page}
            pageSize={pageSize}
            isLoading={isLoading}
          />

          <Pagination
            itemsPerPage={pageSize}
            totalItems={totalLogs}
            paginate={paginate}
            currentPage={page}
          />
        </div>
      </div>
    </Layout>
  );
};
export default ActivityLogsPage;
